import React, {ClipboardEvent, FC, memo, useEffect, useState} from "react";
import {Alert, Button, Drawer, Form, Input, Radio, Space, Upload, message} from "antd";
import {SupportReportKind, useAuthenticateQuery, useSubmitSupportReportMutation} from "../../generated/graphql";
import {ReportContext} from "../../helpers/reportContext";
import {imageFileToJpeg} from "./screenshot";
import i18n from "../../services/i18n";

type SupportReportDrawerProps = {
    /** Beim Öffnen aufgenommene Seite (JPEG-Data-URL); fehlt, wenn die Aufnahme scheiterte */
    screenshot?: string;
    context: ReportContext;
    onClose: () => void;
};

type FormValues = {kind: SupportReportKind; subject: string; description: string};

const SupportReportDrawer: FC<SupportReportDrawerProps> = ({screenshot: captured, context, onClose}) => {
    const tr = i18n.containers.supportReport.SupportReport;
    const [screenshot, setScreenshot] = useState(captured);
    const {data: authData} = useAuthenticateQuery();
    const [submitReport, {loading}] = useSubmitSupportReportMutation();

    const [form] = Form.useForm<FormValues>();
    // useWatch liefert vor dem ersten Render-Durchlauf undefined, der Typ verschweigt das
    const kind = (Form.useWatch("kind", form) as SupportReportKind | undefined) ?? SupportReportKind.Bug;
    const description = Form.useWatch("description", form) as string | undefined;
    const required = [{required: true, whitespace: true, message: tr.required()}];

    // Drei Leitfragen je Art der Meldung; als Vorlage werden sie ins Feld geschrieben
    const guides = {
        [SupportReportKind.Bug]: {subject: tr.subjectPlaceholderBug(), questions: [tr.guideBug1(), tr.guideBug2(), tr.guideBug3()]},
        [SupportReportKind.Idea]: {subject: tr.subjectPlaceholderIdea(), questions: [tr.guideIdea1(), tr.guideIdea2(), tr.guideIdea3()]},
        [SupportReportKind.Question]: {subject: tr.subjectPlaceholderQuestion(), questions: [tr.guideQuestion1(), tr.guideQuestion2(), tr.guideQuestion3()]},
    };
    const guide = guides[kind];
    const templateFor = (reportKind: SupportReportKind) => guides[reportKind].questions.map((question) => `${question}\n\n`).join("\n");
    const insertTemplate = () => form.setFieldsValue({description: templateFor(kind)});

    // Wechselt die Art, während noch die unveränderte Vorlage im Feld steht, folgt die Vorlage.
    // Eigener Text bleibt unangetastet.
    useEffect(() => {
        const current = form.getFieldValue("description") as string | undefined;
        const isUntouchedTemplate = Object.keys(guides).some((reportKind) => current === templateFor(reportKind as SupportReportKind));

        if (isUntouchedTemplate && current !== templateFor(kind)) {
            form.setFieldsValue({description: templateFor(kind)});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind]);

    const submit = async (values: FormValues) => {
        const user = authData?.authenticate;
        const reporterName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || undefined;

        try {
            await submitReport({
                variables: {input: {kind: values.kind, subject: values.subject.trim(), description: values.description.trim(), screenshot, context: JSON.stringify(context), reporterName}},
            });
            void message.success(tr.success(), 6);
            onClose();
        } catch (error) {
            // Formular bleibt offen, der Text geht nicht verloren
            void message.error(tr.error(), 8);
        }
    };

    const applyImage = async (file: Blob) => {
        try {
            setScreenshot(await imageFileToJpeg(file));
        } catch (error) {
            void message.error(tr.screenshotUnreadable());
        }
    };

    const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
        const file = Array.from(event.clipboardData.files).find((candidate) => candidate.type.startsWith("image/"));

        if (file) {
            event.preventDefault();
            void applyImage(file);
        }
    };

    return (
        <Drawer
            open
            title={tr.title()}
            width={560}
            onClose={onClose}
            destroyOnClose
            footer={
                <Space style={{display: "flex", justifyContent: "flex-end"}}>
                    <Button onClick={onClose}>{tr.cancel()}</Button>
                    <Button type="primary" loading={loading} onClick={() => form.submit()} data-testid="support-report-submit">
                        {tr.submit()}
                    </Button>
                </Space>
            }
        >
            <div onPaste={handlePaste}>
                <Form form={form} layout="vertical" initialValues={{kind: SupportReportKind.Bug}} onFinish={submit}>
                    <Form.Item label={tr.kind()} name="kind">
                        <Radio.Group optionType="button">
                            <Radio.Button value={SupportReportKind.Bug}>{tr.kindBug()}</Radio.Button>
                            <Radio.Button value={SupportReportKind.Idea}>{tr.kindIdea()}</Radio.Button>
                            <Radio.Button value={SupportReportKind.Question}>{tr.kindQuestion()}</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item label={tr.subject()} name="subject" rules={required}>
                        <Input placeholder={guide.subject} maxLength={200} data-testid="support-report-subject" />
                    </Form.Item>
                    <Form.Item
                        label={tr.description()}
                        name="description"
                        rules={required}
                        extra={
                            <>
                                {tr.guideIntro()} {guide.questions.join(" ")}
                                {description?.trim() ? null : (
                                    <Button type="link" size="small" onClick={insertTemplate} data-testid="support-report-template">
                                        {tr.insertTemplate()}
                                    </Button>
                                )}
                            </>
                        }
                    >
                        <Input.TextArea placeholder={tr.descriptionPlaceholder()} autoSize={{minRows: 5, maxRows: 14}} maxLength={10000} data-testid="support-report-description" />
                    </Form.Item>
                    <Form.Item label={tr.screenshot()} extra={screenshot !== undefined ? tr.screenshotHint() : undefined}>
                        {screenshot !== undefined ? (
                            <img src={screenshot} alt={tr.screenshot()} style={{width: "100%", border: "1px solid #f0f0f0", borderRadius: 4}} data-testid="support-report-screenshot" />
                        ) : (
                            <Alert type="info" message={tr.screenshotMissing()} showIcon />
                        )}
                        <Space style={{marginTop: 8}}>
                            <Upload
                                accept="image/*"
                                showUploadList={false}
                                beforeUpload={(file) => {
                                    void applyImage(file);

                                    return false;
                                }}
                            >
                                <Button size="small">{tr.screenshotReplace()}</Button>
                            </Upload>
                            {screenshot !== undefined && (
                                <Button size="small" danger onClick={() => setScreenshot(undefined)}>
                                    {tr.screenshotRemove()}
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                    <Alert type="info" message={tr.contextHint()} />
                </Form>
            </div>
        </Drawer>
    );
};

export default memo(SupportReportDrawer);
